import { Document, Page, Text, View, StyleSheet, Image, Link } from "@react-pdf/renderer";
import { createTw } from "react-pdf-tailwind";
import { caseletData, publishedCaselet } from "../../pages/caselet/Caselet";
import { APP_NAME, LINK_TO_PUBLISHED_CASELET_IN_PDF, LINK_TO_UNPUBLISHED_CASELET_IN_PDF } from "../../utils/appConstant";

const appName = `${APP_NAME} - Mentor`;
const tw = createTw({
    theme: {
        extend: {
            colors: {
                custom: "#bada55"
            }
        }
    }
});
const styles = StyleSheet.create({
    heading: {
        fontSize: 22,
        fontWeight: "bold",
        marginBottom: 10,
        color: "#196400"
    },
    topHeading: {
        fontSize: 22,
        fontWeight: "bold",
        marginBottom: 10,
        color: "#1B3C73"
    },
    body: {
        fontSize: 12,
        marginBottom: 10,
        marginLeft: 20
    },
    challenge: {
        fontSize: 18,
        marginBottom: 10,
        marginLeft: 20,
        color: "#006400"
    },
    row: {
        display: "flex",
        flexDirection: "row"
    },
    bullet: {
        marginLeft: 20,
        height: "80%"
    },
    headerRightText: {
        fontSize: 18,
        marginRight: "20px"
    },
    justifyBetween: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    pageNumber: {
        position: "absolute",
        fontSize: 12,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: "center",
        color: "black"
    },
    linkStyle: {
        color: "#378CE7"
    }
});

const ListItem = ({ children }: { children: React.ReactNode }) => {
    return (
        <View style={styles.row }>
            <View style={styles.bullet}>
                <Text>{"\u2022" + " "}</Text>
            </View>
            <Text style={{ fontSize: 18 }}>{children}</Text>
        </View>
    );
};

const CaseletPdf = () => {
    const data = caseletData;
    const published: boolean = publishedCaselet;

    if (!data.summary) {
        return null; 
    }

    return (
        <Document>
            <Page size="A4" style={tw("p-12")}>
                <View style={[tw("p-6 flex"), styles.justifyBetween]}>
                    <Text style={styles.topHeading}>{appName}</Text>
                    <Image src="/favicon.ico" style={tw("h-[71] w-[120px]")} />
                </View>

                <View style={tw("p-6")}>
                    {data.title && (
                        <>
                            <Text style={styles.heading}>Title</Text>
                            <Text style={styles.body}>{data.title}</Text>
                        </>
                    )}
                    {data.summary.background && (
                        <>
                            <Text style={styles.heading}>Background</Text>
                            <Text style={styles.body}>{data.summary.background}</Text>
                        </>
                    )}
                    {data.summary.scenario && (
                        <>
                            <Text style={styles.heading}>Scenario</Text>
                            <Text style={styles.body}>{data.summary.scenario}</Text>
                        </>
                    )}
                    {data.summary.outcome && (
                        <>
                            <Text style={styles.heading}>Outcome</Text>
                            <Text style={styles.body}>{data.summary.outcome}</Text>
                        </>
                    )}
                </View>
                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => ` ${pageNumber}/${totalPages}`} fixed />
            </Page>

            {data.summary.possibleCurrentStates && data.summary.possibleCurrentStates.length > 0 && (
                <Page size="A4" style={tw("p-12")}>
                    <View style={tw("p-8")}>
                        <View style={styles.justifyBetween}>
                            <Text style={styles.topHeading}>{appName}</Text>
                            <Image src="/favicon.ico" style={tw("h-[71] w-[120px]")} />
                        </View>
                        <Text style={styles.heading}>Possible Current Scenario</Text>
                        {data.summary.possibleCurrentStates.map((possibleCurrentState, index) => (
                            <ListItem key={index}> <Text style={styles.body}>{possibleCurrentState}</Text></ListItem>
                        ))}
                    </View>
                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => ` ${pageNumber}/${totalPages}`} fixed />
                </Page>
            )}

            {data.summary.desiredTransformationGoal && data.summary.desiredTransformationGoal.length > 0 && (
                <Page size="A4" style={tw("p-12")}>
                    <View style={tw("p-8")}>
                        <View style={styles.justifyBetween}>
                            <Text style={styles.topHeading}>{appName}</Text>
                            <Image src="/favicon.ico" style={tw("h-[71] w-[120px]")} />
                        </View>
                        <Text style={styles.heading}>Desired Transformation Goals</Text>
                        {data.summary.desiredTransformationGoal.map((goal, index) => (
                            <ListItem key={index}><Text style={styles.body}>{goal}</Text> </ListItem>
                        ))}
                    </View>
                    <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => ` ${pageNumber}/${totalPages}`} fixed />
                </Page>
            )}

            {data.challenges &&
                data.challenges.length > 0 &&
                data.challenges.map((challenge, index) => {
                    if (challenge.challenge) {
                        return (
                            <Page size="A4" style={tw("p-12")} key={index}>
                                <View style={tw("p-8")}>
                                    <View style={styles.justifyBetween}>
                                        <Text style={styles.topHeading}>{appName}</Text>
                                        <Image src="/favicon.ico" style={tw("h-[71] w-[120px]")} />
                                    </View>
                                    <Text style={styles.heading}>{challenge.title}</Text>
                                    <Text style={styles.challenge}>Challenge: </Text>
                                    <Text style={[styles.body, tw("ml-16") ]} >{challenge.challenge}</Text>
                                    {challenge.options &&
                                        challenge.options.length > 0 &&<Text style={styles.challenge}>Options:</Text>}
                                    {challenge.options &&
                                        challenge.options.length > 0 &&
                                        challenge.options.map((option, optionIndex) => <ListItem key={optionIndex}><Text style={styles.body}>{option}</Text></ListItem>)}
                                    <Text
                                        render={({ pageNumber, totalPages }) =>
                                            pageNumber === totalPages && (
                                                <View>
                                                    <Text>
                                                        <Text>Link to the Caselet: </Text>
                                                        <Link
                                                            src={
                                                                !published
                                                                    ? `${LINK_TO_UNPUBLISHED_CASELET_IN_PDF}/${data.id}`
                                                                    : `${LINK_TO_PUBLISHED_CASELET_IN_PDF}/${data.id}`
                                                            }
                                                            style={styles.linkStyle}
                                                        >
                                                            Caselet_{data.id}
                                                        </Link>
                                                    </Text>
                                                </View>
                                            )
                                        }
                                    />
                                </View>
                                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => ` ${pageNumber}/${totalPages}`} fixed />
                            </Page>
                        );
                    } else {
                        return null;
                    }
                })}
        </Document>
    );
};

export default CaseletPdf;

